import { useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import * as Combobox from '@givebutter/primitives/combobox';
import { useToast } from '@givebutter/primitives/toast';
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchCampaigns } from 'api/campaigns';
import { reassignTransaction } from 'api/transactions';
import * as EditableInput from 'components/editable-input';
import * as Form from 'components/form';
import * as Tooltip from 'components/tooltip';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import useDebounce from 'customHooks/useDebounce';
import { useForceRender } from 'customHooks/useForceRender';
import { useInfiniteCampaigns } from 'customHooks/useInfiniteCampaigns';
import { useIsPending } from 'customHooks/useIsPending';
import { uniqBy } from 'lodash';
import { accountQueryKeys } from 'queries/account-query-keys';
import { transactionQueryKeys } from 'queries/transaction-query-keys';
import { Campaign, Transaction } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';
import { PaginatedData } from 'types/pagination';

interface TransactionCampaignProps {
    transaction: Transaction;
}

interface TransactionCampaignFormValues {
    selectedCampaign: string[];
}

const TransactionCampaign = ({ transaction }: TransactionCampaignProps) => {
    const toast = useToast();
    const { accountId } = useCurrentAccount();
    const queryClient = useQueryClient();
    const { key, forceRender } = useForceRender();

    const [searchCampaigns, setSearchCampaigns] = useState('');
    const debouncedSearchCampaigns = useDebounce(searchCampaigns);

    const { data: campaignsData, ref: campaignsRef } = useInfiniteCampaigns({
        query: debouncedSearchCampaigns,
    });

    const methods = Form.useForm<TransactionCampaignFormValues>({
        defaultValues: {
            selectedCampaign: [transaction.campaign.id.toString()],
        },
    });

    const { control, watch, path, handleSubmit, resetFormButton, setError } = methods;

    const rawSelectedCampaign = watch('selectedCampaign');
    const selectedCampaign = rawSelectedCampaign?.[0];

    const [_, params] = useIsPending({
        ids: rawSelectedCampaign.map(Number),
        page: 1,
        per_page: rawSelectedCampaign.length,
    });

    const { data: savedCampaigns } = useSuspenseQuery({
        queryKey: accountQueryKeys.campaignsWithFilters(accountId, params),
        queryFn: () => {
            if (rawSelectedCampaign.length === 0) {
                return Promise.resolve({
                    data: [],
                    links: {} as any,
                    meta: {} as any,
                } as PaginatedData<Campaign>);
            }

            return fetchCampaigns(accountId, params);
        },
    });

    const campaigns = campaignsData?.pages ?? [];
    const displayedCampaigns = uniqBy([...savedCampaigns.data, ...campaigns], 'id');

    const { mutate: submit } = useMutation({
        mutationFn: (_: TransactionCampaignFormValues) =>
            reassignTransaction(transaction?.id, {
                campaign_id: parseInt(selectedCampaign),
            }),
        onSuccess: () => {
            toast.success('Transaction successfully reassigned!');

            queryClient.invalidateQueries({
                queryKey: transactionQueryKeys.transactions(accountId),
            });

            forceRender();
        },
        onError: (error: GivebutterAxiosError) => {
            toast.error(error.response?.data?.message ?? 'Unable to reassign transaction.');

            const campaignIdErrors = error.response?.data.errors?.campaign_id;
            if (campaignIdErrors) {
                setError('selectedCampaign', { message: campaignIdErrors[0] });
            }

            resetFormButton();
        },
    });

    return (
        <div key={key}>
            <EditableInput.Root disabled={!transaction.can_reassign}>
                <EditableInput.ReadOnly className={'flex items-start justify-between'}>
                    <div className={'flex flex-col'}>
                        <div className={'flex items-baseline gap-x-2'}>
                            <p className={'text-sm font-medium text-gray-800'}>Campaign</p>
                            {transaction.can_reassign ? (
                                <EditableInput.ReadOnlyEditTrigger />
                            ) : (
                                <Tooltip.Root>
                                    <Tooltip.Trigger asChild>
                                        <EditableInput.ReadOnlyEditTrigger />
                                    </Tooltip.Trigger>
                                    <Tooltip.Portal>
                                        <Tooltip.Content inverse>
                                            <p>
                                                Currently we can only reassign transactions that
                                                meet the following criteria:
                                            </p>
                                            <ul className="ml-4 list-disc">
                                                <li>
                                                    Has no campaign-specific associations (i.e.
                                                    tickets or matching donations)
                                                </li>
                                                <li>Is external or offline</li>
                                            </ul>
                                            <Tooltip.Arrow inverse />
                                        </Tooltip.Content>
                                    </Tooltip.Portal>
                                </Tooltip.Root>
                            )}
                        </div>
                        <Link
                            className={'flex items-center gap-1'}
                            to={`/accounts/${accountId}/campaigns/${transaction?.campaign?.id}`}
                        >
                            <p>{transaction?.campaign?.title}</p>
                            <FontAwesomeIcon size={'xs'} icon={faArrowRight} />
                        </Link>
                    </div>
                </EditableInput.ReadOnly>
                <EditableInput.Content>
                    <Form.Provider {...methods}>
                        <form
                            onSubmit={handleSubmit(
                                submit as SubmitHandler<TransactionCampaignFormValues>,
                            )}
                            className={'contents'}
                        >
                            <Form.Container>
                                <Form.Label
                                    name={path('selectedCampaign')}
                                    className={'text-gray-800'}
                                >
                                    Reassign campaign
                                </Form.Label>
                                <Form.Combobox
                                    name="selectedCampaign"
                                    control={control}
                                    allowMultiple={false}
                                    rules={{
                                        required: 'Select a campaign',
                                    }}
                                >
                                    <Combobox.Trigger aria-label="Select a campaign">
                                        <Combobox.Select placeholder="Select a campaign" />
                                    </Combobox.Trigger>
                                    <Combobox.Popover>
                                        <Combobox.Search
                                            value={searchCampaigns}
                                            onChangeValue={setSearchCampaigns}
                                        />
                                        <Combobox.Items>
                                            {displayedCampaigns.map((item: Campaign) => (
                                                <Combobox.Item
                                                    value={item.id.toString()}
                                                    key={item.id}
                                                >
                                                    <Combobox.ItemText>
                                                        <span className="text-sm font-medium">
                                                            {item.title}
                                                        </span>
                                                    </Combobox.ItemText>
                                                    <Combobox.CheckmarkIndicator />
                                                </Combobox.Item>
                                            ))}
                                            {campaigns.length === 0 && (
                                                <Combobox.NoneFoundText>
                                                    No campaigns found
                                                </Combobox.NoneFoundText>
                                            )}
                                            <div ref={campaignsRef} />
                                        </Combobox.Items>
                                    </Combobox.Popover>
                                </Form.Combobox>
                                <Form.Error name={path('selectedCampaign')} />
                            </Form.Container>
                            <EditableInput.ActionsContainer>
                                <EditableInput.Cancel />
                                <Form.SubmitButton>Save</Form.SubmitButton>
                            </EditableInput.ActionsContainer>
                        </form>
                    </Form.Provider>
                </EditableInput.Content>
            </EditableInput.Root>
        </div>
    );
};

export { TransactionCampaign };
